import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import useRotatingMessages from "Components/common/hooks/useRotatingMessages";

const CHECKLIST_STEP_MS = 1800;

// Multi-message loaders render as a checklist that ticks forward and parks on
// the last item (a rotating single line hides how far along the work is).
export const useChecklistIndex = (count, active) => {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    setIndex(0);
    if (!active || count < 2) return undefined;
    const id = setInterval(
      () => setIndex(prev => Math.min(prev + 1, count - 1)),
      CHECKLIST_STEP_MS
    );
    return () => clearInterval(id);
  }, [active, count]);
  return index;
};

const ProgressBlock = observer(({ block }) => {
  const running = block.status === "running";
  const messages = block.messages || [];
  const checklist = running && messages.length > 1;
  const rotatingMessage = useRotatingMessages(
    checklist ? [] : messages,
    running,
    CHECKLIST_STEP_MS
  );
  const current = useChecklistIndex(messages.length, checklist);

  // Completed work collapses to one muted line so past steps don't hold the
  // same visual weight as live content.
  if (!running) {
    return (
      <div className={`copilotProgress copilotProgress--${block.status}`}>
        <div className="copilotProgress__head">
          <i className="material-icons-outlined copilotProgress__statusIcon">
            {block.status === "done" ? "check_circle" : "error_outline"}
          </i>
          <span className="copilotProgress__label">{block.label}</span>
          {block.detail && (
            <span className="copilotProgress__detailInline">
              · {block.detail}
            </span>
          )}
        </div>
      </div>
    );
  }

  if (checklist) {
    return (
      <div className="copilotProgress copilotProgress--running">
        <div className="copilotProgress__head">
          <span className="copilotProgress__spinner" />
          <span className="copilotProgress__label">{block.label}</span>
        </div>
        <ul className="copilotProgress__list">
          {messages.map((msg, i) => {
            const state = i < current ? "done" : i === current ? "active" : "todo";
            return (
              <li className={`copilotProgress__item is-${state}`} key={msg}>
                <i className="material-icons-outlined copilotProgress__itemIcon">
                  {state === "done" ? "check_circle" : "radio_button_unchecked"}
                </i>
                <span>{msg}</span>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  return (
    <div className="copilotProgress copilotProgress--running">
      <div className="copilotProgress__head">
        <span className="copilotProgress__spinner" />
        <span className="copilotProgress__label">
          {rotatingMessage || block.label}
        </span>
        {typeof block.percent === "number" && (
          <span className="copilotProgress__percent">{block.percent}%</span>
        )}
      </div>
      {typeof block.percent === "number" && (
        <div className="copilotProgress__bar">
          {/* width via decile classes: inline styles are disallowed */}
          <div
            className={`copilotProgress__barFill fill-${Math.min(
              100,
              Math.max(0, Math.round(block.percent / 10) * 10)
            )}`}
          />
        </div>
      )}
    </div>
  );
});

export default ProgressBlock;
